// Complete CookieConsent.js with modal for user selection

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    shouldShowCookieConsent,
    setCookieConsent,
    trackCookieConsent,
    migrateTokenBasedOnConsent,
    COOKIE_CATEGORIES,
    setCookiePreferences,
    applyExistingConsent,
    checkTrackingToolsAvailability
} from '../../utils/cookieConsent';

const CookieConsent = () => {
    const [show, setShow] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [showRejectWarning, setShowRejectWarning] = useState(false);
    const [showCustomizeModal, setShowCustomizeModal] = useState(false);
    const [preferences, setPreferences] = useState({
        [COOKIE_CATEGORIES.NECESSARY]: true,
        [COOKIE_CATEGORIES.ANALYTICS]: false,
        [COOKIE_CATEGORIES.MARKETING]: false,
        [COOKIE_CATEGORIES.PREFERENCES]: false
    });

    useEffect(() => {
        // Check if user has already made a choice
        if (shouldShowCookieConsent()) {
            setShow(true);
            // Add slight delay for smooth animation
            setTimeout(() => setIsVisible(true), 100);
        } else {
            // User has already made a choice, apply existing consent
            checkTrackingToolsAvailability();
            applyExistingConsent();
        }
    }, []);

    const enableTrackingTools = (enabledCategories) => {
        // Enable/disable tracking tools based on categories
        if (enabledCategories[COOKIE_CATEGORIES.ANALYTICS]) {
            enableAnalyticsTools();
        } else {
            disableAnalyticsTools();
        }

        if (enabledCategories[COOKIE_CATEGORIES.MARKETING]) {
            enableMarketingTools();
        } else {
            disableMarketingTools();
        }
    };

    const enableAnalyticsTools = () => {
        // Google Analytics
        if (window.gtag) {
            window.gtag('consent', 'update', {
                'analytics_storage': 'granted'
            });
        }

        // Google Tag Manager for Analytics
        if (window.dataLayer) {
            window.dataLayer.push({
                'event': 'consent_update',
                'analytics_storage': 'granted'
            });
        }

        // LINE Tag Analytics
        if (window._lt) {
            window._lt('send', 'pv', ['d17aff9b-8b5b-4786-b32e-4d3e9b6ce1a5']);
        }
    };

    const disableAnalyticsTools = () => {
        // Google Analytics
        if (window.gtag) {
            window.gtag('consent', 'update', {
                'analytics_storage': 'denied'
            });
        }

        // Google Tag Manager
        if (window.dataLayer) {
            window.dataLayer.push({
                'event': 'consent_update',
                'analytics_storage': 'denied'
            });
        }
    };

    const enableMarketingTools = () => {
        // Facebook Pixel
        if (window.fbq) {
            window.fbq('consent', 'grant');
            window.fbq('track', 'PageView'); // Track initial page view
        }

        // TikTok Pixel
        if (window.ttq) {
            window.ttq.enableCookie();
            window.ttq.page(); // Track initial page view
        }

        // Google Tag Manager for Marketing
        if (window.dataLayer) {
            window.dataLayer.push({
                'event': 'consent_update',
                'ad_storage': 'granted',
                'ad_user_data': 'granted',
                'ad_personalization': 'granted'
            });
        }
    };

    const disableMarketingTools = () => {
        // Facebook Pixel
        if (window.fbq) {
            window.fbq('consent', 'revoke');
        }

        // TikTok Pixel
        if (window.ttq) {
            window.ttq.disableCookie();
        }

        // Google Tag Manager
        if (window.dataLayer) {
            window.dataLayer.push({
                'event': 'consent_update',
                'ad_storage': 'denied',
                'ad_user_data': 'denied',
                'ad_personalization': 'denied'
            });
        }
    };

    const handleAccept = () => {
        const allAcceptedPreferences = {
            [COOKIE_CATEGORIES.NECESSARY]: true,
            [COOKIE_CATEGORIES.ANALYTICS]: true,
            [COOKIE_CATEGORIES.MARKETING]: true,
            [COOKIE_CATEGORIES.PREFERENCES]: true
        };

        setCookieConsent('accepted');
        setCookiePreferences(allAcceptedPreferences);
        enableTrackingTools(allAcceptedPreferences);
        trackCookieConsent('accept_all');
        migrateTokenBasedOnConsent();
        setShow(false);

    };

    const handleReject = () => {
        // Show warning about functionality limitations
        setShowRejectWarning(true);
    };

    const confirmReject = () => {
        const rejectedPreferences = {
            [COOKIE_CATEGORIES.NECESSARY]: true, // Always true
            [COOKIE_CATEGORIES.ANALYTICS]: false,
            [COOKIE_CATEGORIES.MARKETING]: false,
            [COOKIE_CATEGORIES.PREFERENCES]: false
        };

        setCookieConsent('rejected');
        setCookiePreferences(rejectedPreferences);
        enableTrackingTools(rejectedPreferences); // This will disable analytics and marketing
        trackCookieConsent('reject_all');

        // Migrate token to sessionStorage for users who reject cookies
        migrateTokenBasedOnConsent();

        // Clear any analytics/marketing cookies
        clearNonEssentialCookies();

        setShowRejectWarning(false);
        setShow(false);



        // Show user notification about limited functionality
        showLimitedFunctionalityMessage();
    };

    const handleCustomize = () => {
        trackCookieConsent('customize');
        setShowCustomizeModal(true);
    };

    const handlePreferenceChange = (category, value) => {
        setPreferences(prev => ({
            ...prev,
            [category]: value
        }));
    };

    const handleSavePreferences = () => {
        setCookiePreferences(preferences);
        setCookieConsent('customized');
        enableTrackingTools(preferences);
        trackCookieConsent('customize_save', {
            analytics: preferences[COOKIE_CATEGORIES.ANALYTICS],
            marketing: preferences[COOKIE_CATEGORIES.MARKETING],
            preferences: preferences[COOKIE_CATEGORIES.PREFERENCES]
        });

        // Migrate token if needed
        migrateTokenBasedOnConsent();

        setShowCustomizeModal(false);
        setShow(false);

    };

    const handleCancelCustomize = () => {
        setShowCustomizeModal(false);
        // Reset preferences to default
        setPreferences({
            [COOKIE_CATEGORIES.NECESSARY]: true,
            [COOKIE_CATEGORIES.ANALYTICS]: false,
            [COOKIE_CATEGORIES.MARKETING]: false,
            [COOKIE_CATEGORIES.PREFERENCES]: false
        });
    };

    const clearNonEssentialCookies = () => {
        // Clear analytics cookies
        document.cookie = '_ga=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        document.cookie = '_gid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        document.cookie = '_fbp=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

        // Clear any other non-essential cookies
        const cookies = document.cookie.split(';');
        cookies.forEach(cookie => {
            const [name] = cookie.split('=');
            if (name.trim() && !name.trim().startsWith('token') && !name.trim().startsWith('admin_token')) {
                document.cookie = `${name.trim()}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            }
        });
    };

    const showLimitedFunctionalityMessage = () => {
        // Show a temporary message about limited functionality
        const message = document.createElement('div');
        message.className = 'cookie-reject-notification';
        message.innerHTML = `
            <div style="
                position: fixed;
                top: 20px;
                right: 20px;
                background: #fff3cd;
                border: 1px solid #ffeaa7;
                border-radius: 8px;
                padding: 1rem;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                z-index: 10000;
                max-width: 300px;
            ">
                <h4 style="margin: 0 0 0.5rem 0; color: #856404;">⚠️ ฟังก์ชันจำกัด</h4>
                <p style="margin: 0; font-size: 0.9rem; color: #856404;">
                    การปฏิเสธคุกกี้จะทำให้คุณต้องเข้าสู่ระบบใหม่ทุกครั้งที่ปิดเบราว์เซอร์
                </p>
                <button onclick="this.parentElement.remove()" style="
                    margin-top: 0.5rem;
                    background: #856404;
                    color: white;
                    border: none;
                    padding: 0.25rem 0.5rem;
                    border-radius: 4px;
                    cursor: pointer;
                ">เข้าใจแล้ว</button>
            </div>
        `;
        document.body.appendChild(message);

        // Auto-remove after 10 seconds
        setTimeout(() => {
            if (message.parentElement) {
                message.remove();
            }
        }, 10000);
    };

    const CookieCategoryItem = ({ category, title, description, isRequired = false }) => (
        <div style={{
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem',
            backgroundColor: '#f8f9fa'
        }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>
                            {title}
                            {isRequired && <span style={{ color: '#dc3545', marginLeft: '0.25rem' }}>*</span>}
                        </h4>
                        {isRequired && (
                            <span style={{
                                fontSize: '0.75rem',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                padding: '0.125rem 0.375rem',
                                borderRadius: '4px'
                            }}>
                                จำเป็น
                            </span>
                        )}
                    </div>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#6c757d', lineHeight: '1.4' }}>
                        {description}
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: isRequired ? 'not-allowed' : 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={preferences[category]}
                            onChange={(e) => !isRequired && handlePreferenceChange(category, e.target.checked)}
                            disabled={isRequired}
                            style={{
                                width: '1.25rem',
                                height: '1.25rem',
                                marginRight: '0.5rem'
                            }}
                        />
                        <span style={{ fontSize: '0.875rem' }}>
                            {isRequired ? 'เปิดใช้งาน' : preferences[category] ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                        </span>
                    </label>
                </div>
            </div>
        </div>
    );

    if (!show) return null;

    return (
        <>
            <div
                className={`cookie-consent ${isVisible ? 'visible' : ''}`}
                role="dialog"
                aria-labelledby="cookie-consent-title"
                aria-describedby="cookie-consent-description"
            >
                <div className="cookie-consent-container">
                    <div className="cookie-consent-content">
                        <div className="cookie-consent-header">
                            <FontAwesomeIcon
                                icon={['fas', 'cookie-bite']}
                                className="cookie-icon"
                                aria-hidden="true"
                            />
                            <h3 id="cookie-consent-title" className="cookie-consent-title">
                                คุกกี้
                            </h3>
                        </div>

                        <div id="cookie-consent-description" className="cookie-consent-description">
                            <p>
                                เราใช้คุกกี้เพื่อปรับปรุงประสบการณ์การใช้งานของคุณ
                            </p>
                            <p>
                                <Link to="/cookie-policy" className="cookie-policy-link">
                                    อ่านนโยบายคุกกี้
                                </Link>
                            </p>
                        </div>

                        <div className="cookie-consent-actions">
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm cookie-btn-reject"
                                onClick={handleReject}
                                aria-label="ปฏิเสธคุกกี้ที่ไม่จำเป็น"
                            >
                                ปฏิเสธ
                            </button>

                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm cookie-btn-customize"
                                onClick={handleCustomize}
                                aria-label="ปรับแต่งการตั้งค่าคุกกี้"
                            >
                                ปรับแต่ง
                            </button>

                            <button
                                type="button"
                                className="btn btn-primary btn-sm cookie-btn-accept"
                                onClick={handleAccept}
                                aria-label="ยอมรับคุกกี้ทั้งหมด"
                            >
                                ยอมรับทั้งหมด
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Reject Warning Modal */}
            {showRejectWarning && (
                <div className="cookie-reject-modal" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10001
                }}>
                    <div style={{
                        background: 'white',
                        padding: '2rem',
                        borderRadius: '12px',
                        maxWidth: '400px',
                        margin: '1rem',
                        textAlign: 'center'
                    }}>
                        <h4 style={{ color: '#dc3545', marginBottom: '1rem' }}>
                            <span role="img" aria-label="warning">⚠️</span> ฟังก์ชันจำกัด
                        </h4>
                        <p style={{ marginBottom: '1.5rem', lineHeight: '1.5' }}>
                            การปฏิเสธคุกกี้จะทำให้คุณต้องเข้าสู่ระบบใหม่ทุกครั้งที่ปิดเบราว์เซอร์
                            และไม่สามารถใช้ฟีเจอร์บางอย่างได้
                        </p>
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                            <button
                                onClick={() => setShowRejectWarning(false)}
                                style={{
                                    padding: '0.5rem 1rem',
                                    border: '1px solid #6c757d',
                                    background: 'white',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                ยกเลิก
                            </button>
                            <button
                                onClick={confirmReject}
                                style={{
                                    padding: '0.5rem 1rem',
                                    border: 'none',
                                    background: '#dc3545',
                                    color: 'white',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                ปฏิเสธคุกกี้
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Customize Preferences Modal */}
            {showCustomizeModal && (
                <div className="cookie-customize-modal" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10001,
                    padding: '1rem'
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '12px',
                        maxWidth: '600px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflow: 'auto'
                    }}>
                        {/* Header */}
                        <div style={{
                            padding: '1.5rem 1.5rem 1rem 1.5rem',
                            borderBottom: '1px solid #e9ecef',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                                <FontAwesomeIcon icon={['fas', 'cog']} style={{ marginRight: '0.5rem' }} />
                                ปรับแต่งการตั้งค่าคุกกี้
                            </h3>
                            <button
                                onClick={handleCancelCustomize}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: '#6c757d'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        {/* Content */}
                        <div style={{ padding: '1.5rem' }}>
                            <p style={{ marginBottom: '1.5rem', color: '#6c757d', fontSize: '0.9rem' }}>
                                เลือกประเภทคุกกี้ที่คุณต้องการให้เว็บไซต์ใช้
                            </p>

                            <CookieCategoryItem
                                category={COOKIE_CATEGORIES.NECESSARY}
                                title="คุกกี้ที่จำเป็น"
                                description="คุกกี้เหล่านี้จำเป็นสำหรับการทำงานของเว็บไซต์ เช่น การเข้าสู่ระบบ การรักษาความปลอดภัย และการทำงานพื้นฐานของระบบ"
                                isRequired={true}
                            />

                            <CookieCategoryItem
                                category={COOKIE_CATEGORIES.PREFERENCES}
                                title="คุกกี้การตั้งค่า"
                                description="คุกกี้เหล่านี้ช่วยให้เว็บไซต์จดจำการตั้งค่าของคุณ เช่น ภาษา การตั้งค่าการแสดงผล และการตั้งค่าส่วนตัวอื่นๆ"
                            />

                            <CookieCategoryItem
                                category={COOKIE_CATEGORIES.ANALYTICS}
                                title="คุกกี้การวิเคราะห์"
                                description="คุกกี้เหล่านี้ช่วยให้เราเข้าใจว่าผู้ใช้ใช้งานเว็บไซต์อย่างไร เพื่อปรับปรุงประสบการณ์การใช้งาน"
                            />

                            <CookieCategoryItem
                                category={COOKIE_CATEGORIES.MARKETING}
                                title="คุกกี้การตลาด"
                                description="คุกกี้เหล่านี้ใช้สำหรับแสดงโฆษณาที่เกี่ยวข้องกับความสนใจของคุณ และติดตามประสิทธิภาพของแคมเปญการตลาด"
                            />
                        </div>

                        {/* Footer */}
                        <div style={{
                            padding: '1rem 1.5rem 1.5rem 1.5rem',
                            borderTop: '1px solid #e9ecef',
                            display: 'flex',
                            gap: '1rem',
                            justifyContent: 'flex-end'
                        }}>
                            <button
                                onClick={handleCancelCustomize}
                                style={{
                                    padding: '0.5rem 1rem',
                                    border: '1px solid #6c757d',
                                    background: 'white',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                ยกเลิก
                            </button>
                            <button
                                onClick={handleSavePreferences}
                                style={{
                                    padding: '0.5rem 1rem',
                                    border: 'none',
                                    background: '#007bff',
                                    color: 'white',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                บันทึกการตั้งค่า
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CookieConsent;